/**
 * ReassignProfileModal — Manager Handoff (#75 follow-up / Item 18).
 *
 * Reassigns one Predisposition Profile to another teammate on the same
 * workspace. Per the Manager Handoff design lock (decision 3): the Profile,
 * its observed tags, and its career-arc events travel with it; the prior
 * owner's free-text private notes stay behind with their author. Consent is
 * tenant-scoped and carries on an internal handoff — the subject is not
 * re-prompted (decision 4).
 *
 * The recipient list comes from /api/tenant-users-list (any member may read
 * it). The reassignment itself is authorized server-side to the Profile's
 * owner or a workspace admin.
 *
 * --ppr-* tokens only.
 */
import { useEffect, useState } from "react";
import { useSession } from "../lib/useSession.js";
import { fetchJson, parseJsonResponse } from "../lib/api-fetch.js";

const fn = (name) => `/.netlify/functions/${name}`;

export default function ReassignProfileModal({ profile, onClose, onReassigned }) {
  const { session } = useSession();
  const bearer = session?.access_token;

  const [users, setUsers] = useState(null); // null = loading
  const [recipient, setRecipient] = useState("");
  // Decision 6 (C8) — the opt-in private-notes toggle. Default off.
  const [includeNotes, setIncludeNotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  // Load the workspace roster for the recipient picker.
  useEffect(() => {
    if (!bearer) return;
    let active = true;
    fetchJson(fn("tenant-users-list"), {
      headers: { authorization: `Bearer ${bearer}` },
    }).then(({ ok, data, error }) => {
      if (!active) return;
      if (!ok) {
        setErr(error || "Couldn't load the teammate list.");
        setUsers([]);
      } else {
        setUsers(data.users || []);
      }
    });
    return () => {
      active = false;
    };
  }, [bearer]);

  // The current owner cannot be the recipient.
  const candidates = (users || []).filter(
    (u) => u.user_id !== profile.owner_user_id,
  );

  const submit = async () => {
    if (!recipient) {
      setErr("Choose a teammate to hand this Profile to.");
      return;
    }
    setErr(null);
    setSubmitting(true);
    let res;
    try {
      res = await fetch(fn("profile-reassign"), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({
          profile_id: profile.internal_uuid,
          new_owner_user_id: recipient,
          include_notes: includeNotes,
        }),
      });
    } catch (e) {
      setSubmitting(false);
      setErr(e.message || "Network error");
      return;
    }
    const body = await parseJsonResponse(res);
    setSubmitting(false);
    if (!res.ok) {
      setErr(body.error || "Couldn't reassign the Profile. Try again.");
      return;
    }
    onReassigned?.(recipient);
    onClose();
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Reassign Profile"
      >
        <div style={headStyle}>
          <p style={titleStyle}>Reassign Profile</p>
          <p style={subStyle}>
            Hand <strong>{profile.display_name || "this Profile"}</strong> to
            another teammate on the workspace.
          </p>
        </div>

        <div style={bodyStyle}>
          <label style={fieldLabelStyle} htmlFor="reassign-recipient">
            New owner
          </label>
          <select
            id="reassign-recipient"
            value={recipient}
            disabled={submitting || users === null}
            onChange={(e) => setRecipient(e.target.value)}
            style={selectStyle}
          >
            <option value="">
              {users === null ? "Loading teammates…" : "Choose a teammate…"}
            </option>
            {candidates.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.display_name || "Unnamed teammate"}
              </option>
            ))}
          </select>

          {users !== null && candidates.length === 0 && (
            <p style={noteMutedStyle}>
              There's no one else on this workspace yet. Invite a teammate
              from Settings → Team first.
            </p>
          )}

          <div style={transferNoteStyle}>
            <p style={transferLineStyle}>
              <strong>Moves with the Profile:</strong> the Predisposition
              Profile, its observed tags, and its career-arc history.
            </p>
            <p style={transferLineStyle}>
              <strong>Stays behind by default:</strong> the prior owner's
              private notes — unless you choose to pass them with the toggle
              below.
            </p>
            <p style={{ ...transferLineStyle, margin: 0 }}>
              The subject already consented to PeoplePrints within this
              organization, so an internal handoff doesn't ask them to
              re-consent.
            </p>
          </div>

          <label style={toggleRowStyle}>
            <input
              type="checkbox"
              checked={includeNotes}
              onChange={(e) => setIncludeNotes(e.target.checked)}
              disabled={submitting}
              style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer", flexShrink: 0 }}
              data-testid="reassign-include-notes"
            />
            <span style={toggleTextStyle}>
              <strong>Also pass my private notes.</strong> Off by default —
              your notes stay with you. Turn this on to hand them to the new
              owner as attributed, read-only context — shown as your notes,
              not folded into theirs.
            </span>
          </label>

          {err && (
            <div style={errStyle} role="alert">
              {err}
            </div>
          )}
        </div>

        <div style={footStyle}>
          <button
            type="button"
            style={btnGhostStyle}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={btnNavyStyle}
            onClick={submit}
            disabled={submitting || !recipient}
          >
            {submitting ? "Reassigning…" : "Reassign Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────────── */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 27, 46, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  zIndex: "var(--ppr-z-modal)",
};

const cardStyle = {
  background: "var(--ppr-color-background-surface)",
  borderRadius: "var(--ppr-radius-lg)",
  width: "100%",
  maxWidth: 460,
  boxShadow: "0 24px 60px rgba(15, 27, 46, 0.35)",
};

const headStyle = {
  padding: "22px 26px 16px",
  borderBottom: "1px solid var(--ppr-color-border-default)",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "18px",
  color: "var(--ppr-color-accent-navy)",
  margin: 0,
};

const subStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "13px",
  color: "var(--ppr-color-text-secondary)",
  margin: "5px 0 0",
};

const bodyStyle = { padding: "22px 26px" };

const footStyle = {
  padding: "16px 26px 22px",
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
};

const fieldLabelStyle = {
  display: "block",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "12.5px",
  fontWeight: 700,
  color: "var(--ppr-color-accent-navy)",
  marginBottom: 6,
};

const selectStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "14px",
  color: "var(--ppr-color-text-primary)",
  background: "var(--ppr-color-background-surface)",
  boxSizing: "border-box",
};

const noteMutedStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "12px",
  color: "var(--ppr-color-text-muted)",
  margin: "8px 0 0",
};

const transferNoteStyle = {
  marginTop: 16,
  padding: "12px 14px",
  borderLeft: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-pale-soft)",
};

const transferLineStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "12.5px",
  lineHeight: "var(--ppr-typography-line-height-snug)",
  color: "var(--ppr-color-text-primary)",
  margin: "0 0 8px",
};

const toggleRowStyle = {
  marginTop: 14,
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  cursor: "pointer",
};

const toggleTextStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "12.5px",
  lineHeight: "var(--ppr-typography-line-height-snug)",
  color: "var(--ppr-color-text-primary)",
};

const errStyle = {
  marginTop: 14,
  padding: "10px 12px",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral-very-soft)",
  color: "var(--ppr-color-accent-coral-dark)",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "12.5px",
};

const btnNavyStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontWeight: 600,
  fontSize: "14px",
  borderRadius: "var(--ppr-radius-md)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  padding: "10px 18px",
  cursor: "pointer",
};

const btnGhostStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontWeight: 600,
  fontSize: "14px",
  borderRadius: "var(--ppr-radius-md)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "transparent",
  color: "var(--ppr-color-accent-navy)",
  padding: "10px 18px",
  cursor: "pointer",
};
